import type { CatalogJobInput } from "./catalogModel";
import type { BomItem, ForecastState } from "../types";
import { machineLoads, type MachineLoad } from "./capacity";
import type { ErpStockItem, WorkOrder } from "./erpApi";
import type { JobListItem } from "./jobApi";
import type { JobRecord } from "./jobTypes";
import type { MailMessage } from "./mailApi";
import type { HighwayRunSummary, HighwayRunsResponse, OutcomeReason, RfqLine, RfqRecord, RfqStats } from "./rfqApi";
import type { Machine, WorkOrderCost, WorkOrderMaterial, WorkOrderOperation } from "./shopfloorApi";

/** Browser / Playwright smoke mode. */
export function isE2eMockMode(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get("e2e") === "1" || q.get("e2e") === "true") return true;
  } catch {
    /* ignore */
  }
  return import.meta.env.VITE_E2E_MOCK === "1" || import.meta.env.VITE_E2E_MOCK === "true";
}

/**
 * Offline stand-in for a 404 `erp_v2_disabled`. Only honoured in mock mode,
 * so a real session cannot hide live work orders with a query flag.
 */
export function isE2eShopOff(): boolean {
  if (!isE2eMockMode() || typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("shop") === "off";
  } catch {
    return false;
  }
}

const items: BomItem[] = [
  {
    item_no: 1,
    part_no: "GOVDE-340X92X40",
    qty: 1,
    description: "Kızak Mekanizması Gövdesi",
    material: "S355",
    unit: "adet",
    confidence: 0.88,
    risk_level: "YELLOW",
    suggested_action: "MAKE_INTERNAL",
    est_material_cost_try: 720,
    raw_row_data: {
      source: "step_sub_solid",
      solid_index: 0,
      volume_cm3: 593,
      label_hint: "Solid_1",
      inferred_part_type: "govde",
    },
    matched_stock: {
      part_no: "ERP-GOVDE-01",
      description: "Gövde stok",
      match_score: 92,
      stock_qty: 4,
      match_type: "preference",
      unit_price_try: 720,
      candidates: [
        { part_no: "ERP-GOVDE-01", match_score: 92, stock_qty: 4 },
        { part_no: "ERP-GOVDE-02", match_score: 70, stock_qty: 1 },
      ],
    },
  },
  {
    item_no: 2,
    part_no: "KOL-325X80X50",
    qty: 1,
    description: "Ana Bağlantı Kolu",
    material: "S355",
    unit: "adet",
    confidence: 0.82,
    risk_level: "YELLOW",
    suggested_action: "MAKE_INTERNAL",
    // Line cost without preferred stock — keeps primary title as part_no in UI.
    est_material_cost_try: 480,
    raw_row_data: {
      source: "step_sub_solid",
      solid_index: 1,
      volume_cm3: 347,
      label_hint: "Solid_2",
      inferred_part_type: "buyuk bilesen",
    },
  },
];

/** Static preview for the offline drawings catalog. Not an engine artifact. */
export const E2E_DRAWING_THUMB = "/demo/slicrank-thumb.svg";

/**
 * Offline drawings list (`/drawings?e2e=1`).
 * One card points at a bundled preview; the DXF has no bake, so the list
 * shows the file-type placeholder instead of requesting a missing part PNG.
 */
export function buildE2eDrawingsCatalog(): CatalogJobInput[] {
  const stepItems: BomItem[] = [
    { ...items[0], thumbnail_url: E2E_DRAWING_THUMB },
    { ...items[1], thumbnail_url: null },
  ];
  const dxfItems: BomItem[] = [
    {
      item_no: 1,
      part_no: "PLATE-100",
      qty: 2,
      description: "Cover plate",
      material: "S235",
      unit: "adet",
      confidence: 0.7,
      thumbnail_url: null,
    },
  ];
  return [
    {
      drawing_id: "e2e-drawing-step",
      job_id: "e2e-drawing-step",
      name: "SLICRANK MECHANISM.STEP",
      file_path: "SLICRANK MECHANISM.STEP",
      status: "ready",
      items: stepItems,
      created_at: "2026-08-10T14:02:00Z",
      updated_at: "2026-08-10T14:02:00Z",
      has_thumbnail: true,
      thumbnail_url: E2E_DRAWING_THUMB,
    },
    {
      drawing_id: "e2e-drawing-dxf",
      job_id: "e2e-drawing-dxf",
      name: "HOUSING-340-L.dxf",
      file_path: "HOUSING-340-L.dxf",
      status: "ready",
      items: dxfItems,
      created_at: "2026-08-09T09:00:00Z",
      updated_at: "2026-08-09T09:00:00Z",
      has_thumbnail: false,
      thumbnail_url: null,
    },
  ];
}

/**
 * Six uploads of one STEP name for the AI context picker (`/?e2e=1`).
 * `created_at` is the only field on a job-list row that tells them apart.
 * Newest first, matching `GET /v1/jobs` order.
 */
export function buildE2eAiJobs(): JobListItem[] {
  const name = "flange-dn50.step";
  const stamps = [
    "2026-10-01T13:10:00Z",
    "2026-09-28T07:50:00Z",
    "2026-09-21T16:22:00Z",
    "2026-09-14T09:05:00Z",
    "2026-09-08T11:40:00Z",
    "2026-09-02T08:15:00Z",
  ];
  return stamps.map((created_at, index) => ({
    job_id: `e2e-ai-job-${index + 1}`,
    file_name: name,
    drawing_id: name,
    status: "ready",
    created_at,
    updated_at: created_at,
  }));
}

/** Synthetic assembly job so Review/HITL chrome works offline (?e2e=1 / demo). */
export function buildE2eHitlJob(drawingId = "e2e-slicrank"): JobRecord {
  return {
    job_id: "e2e-job-1",
    status: "ready",
    master_path: "e2e://SLICRANK MECHANISM.STEP",
    files: [{ path: "e2e://SLICRANK MECHANISM.STEP", role: "master" }],
    drawing_id: drawingId,
    quote_blocked: true,
    quote_blockers: ["hitl_open:1"],
    low_confidence_rows: [1],
    confidence_overall: 0.85,
    offline: true,
    items: [
      {
        item_no: 1,
        part_no: "GOVDE-340X92X40",
        qty: 1,
        material: "S355",
        confidence: 0.55,
        node_id: "n_govde",
        description: "Kızak Mekanizması Gövdesi",
        matched_stock: items[0].matched_stock ?? null,
      },
      {
        item_no: 2,
        part_no: "KOL-325X80X50",
        qty: 1,
        material: "S355",
        confidence: 0.82,
        node_id: "n_kol",
        description: "Ana Bağlantı Kolu",
      },
    ],
    assembly_tree: {
      root_node_ids: ["n_govde", "n_kol"],
      solid_count: 2,
      nodes: {
        n_govde: {
          node_id: "n_govde",
          name: "GOVDE-340X92X40",
          solid_index: 0,
          bom_item_no: 1,
          hitl_required: true,
          hitl_reasons: ["low_confidence"],
        },
        n_kol: {
          node_id: "n_kol",
          name: "KOL-325X80X50",
          solid_index: 1,
          bom_item_no: 2,
          hitl_required: false,
        },
      },
    },
  };
}

/** Offline RFQ so the v2 journey strip and step bodies render without the engine. */
export function buildE2eRfq(): RfqRecord {
  return {
    id: "e2e-rfq-1",
    rfq_no: "RFQ-E2E-1",
    title: "SLICRANK MECHANISM",
    status: "draft",
    line_count: 2,
    pending_count: 1,
    decided_count: 1,
    customer_name: "Acme",
    customer_email: "buyer@acme.test",
    due_at: "2026-10-15T00:00:00Z",
    est_hours: 4.5,
    est_lead_days: 12,
    est_cost_try: 2543,
    suggested_margin_pct: 18,
    margin_reason: "varsayılan %18; henüz kazanma oranı yok",
    quote_currency: null,
    target_price: null,
    outside_processes: [],
    created_at: "2026-09-01T10:00:00Z",
    sources: [
      {
        id: "e2e-src-step",
        kind: "cad",
        read_state: "read",
        original_name: "SLICRANK MECHANISM.STEP",
        source_job_id: "e2e-job-1",
      },
    ],
    lines: [
      {
        id: "e2e-line-1",
        line_no: 1,
        kind: "part",
        source_kind: "upload_job",
        part_no: "GOVDE-340X92X40",
        description: "Kızak Mekanizması Gövdesi",
        qty: 1,
        make_buy: "make",
        process_code: "FREZE",
        material: "S355",
        // Priced the way the engine returns a line: material batch, hourly op
        // (setup + run) and a per-area outside op. They sum to est_cost_try.
        material_json: { mass_kg: 4.43, try_per_kg: 100, source: "intel" },
        material_cost_try: 443,
        cost_json: { qty: 1, material_cost_try: 443, ops_cost_try: 1620, total_try: 2063, hours: 2.75 },
        route_json: [
          {
            op: "FREZE",
            machine: "Mill 1",
            est_hours: 1,
            setup_hrs: 0.5,
            run_hrs_per_unit: 0.5,
            rate_try_per_hr: 1200,
            rate_source: "machines",
            setup_cost_try: 600,
            run_cost_try: 600,
            unit_total_try: 1200,
            cost_try: 1200,
            overridden: true,
            params: { setup_min: 30, cycle_min: 30, holes: 4 },
          },
          {
            op: "KAPLAMA",
            pricing_mode: "per_area",
            est_hours: 0,
            setup_cost_try: 0,
            run_cost_try: 420,
            unit_total_try: 420,
            cost_try: 420,
            params: { area_m2: 0.84, cost_try_per_m2: 500 },
          },
        ],
        est_hours: 2.75,
        est_cost_try: 2063,
        est_lead_days: 7,
        decision_status: "decided",
        source_job_id: "e2e-job-1",
        source_item_no: 1,
      },
      {
        id: "e2e-line-2",
        line_no: 2,
        kind: "part",
        source_kind: "upload_job",
        part_no: "KOL-325X80X50",
        description: "Ana Bağlantı Kolu",
        qty: 1,
        make_buy: "buy",
        route_json: [],
        est_hours: 1.75,
        est_cost_try: 480,
        decision_status: "pending",
        source_job_id: "e2e-job-1",
        source_item_no: 2,
        supplier_ask: { status: "draft", supplier: "Öz Metal" },
      },
    ],
  };
}

/**
 * Offline stand-in for a new RFQ on the quote check (`/rfq/e2e-triage/triage?e2e=1`).
 * Findings are not stored here: demo mode derives them with the engine's rules.
 */
export const E2E_TRIAGE_ID = "e2e-triage";

export function buildE2eTriageRfq(): RfqRecord {
  const line = (partial: Partial<RfqLine> & Pick<RfqLine, "id" | "line_no" | "part_no">): RfqLine => ({
    kind: "part",
    source_kind: "intake",
    qty: 1,
    make_buy: "undecided",
    decision_status: "pending",
    route_json: [],
    description: partial.part_no,
    ...partial,
  });
  return {
    id: E2E_TRIAGE_ID,
    rfq_no: "RFQ-1203",
    title: "Conveyor roller shaft",
    status: "waiting_customer",
    customer_name: "Kerem Aydın",
    customer_email: "satinalma@atlaskonveyor.com.tr",
    due_at: "2026-10-20T00:00:00Z",
    waiting_at: "2026-10-01T10:05:00",
    created_at: "2026-10-01T09:12:00",
    line_count: 4,
    part_count: 4,
    pending_count: 4,
    decided_count: 0,
    customer_replies: [],
    sources: [
      { id: "e2e-triage-src-1", kind: "cad", read_state: "read", original_name: "shaft.step" },
      { id: "e2e-triage-src-2", kind: "text", read_state: "read", original_name: "mail.eml" },
      { id: "e2e-triage-src-3", kind: "pdf", read_state: "unread", original_name: "note.pdf" },
    ],
    lines: [
      line({
        id: "e2e-triage-1",
        line_no: 1,
        part_no: "SHF-002",
        description: "Roller shaft",
        qty: 0,
        material: null,
        source_job_id: null,
        stock_item_id: null,
      }),
      line({
        id: "e2e-triage-2",
        line_no: 2,
        part_no: "SHF-001",
        description: "Roller",
        qty: 50,
        material: "C45",
        source_job_id: "e2e-job-1",
        stock_item_id: 1,
      }),
      line({
        id: "e2e-triage-3",
        line_no: 3,
        part_no: "SHF-004",
        description: "Bearing housing",
        qty: 4,
        material: "",
        source_job_id: "e2e-job-1",
      }),
      line({
        id: "e2e-triage-4",
        line_no: 4,
        part_no: "SHF-003",
        description: "End cap",
        qty: 10,
        material: "S235",
        source_job_id: null,
        stock_item_id: null,
      }),
    ],
  };
}

/**
 * Demo stand-in for RFQ 0015 (`?e2e=1` only). Three header fields the desk
 * already treats as missing, and three demand lines with no stock match.
 * Part numbers are the offline slicrank fixture, not a copy of the live RFQ.
 */
export const E2E_QUOTE_BLOCKED_ID = "10eaa9dd-59e1-45ef-bb3a-5e735378fd29";

export function buildE2eQuoteBlockedRfq(): RfqRecord {
  const base = buildE2eRfq();
  const [govde, kol] = base.lines;
  const third: RfqLine = {
    ...kol,
    id: "e2e-quote-line-3",
    line_no: 3,
    part_no: "ERP-GOVDE-02",
    description: "Gövde stok",
    qty: 1,
    material: "S355",
    stock_item_id: null,
    make_buy: "undecided",
    source_item_no: null,
    supplier_ask: null,
  };
  return {
    ...base,
    id: E2E_QUOTE_BLOCKED_ID,
    rfq_no: "0015",
    customer_name: null,
    customer_email: null,
    due_at: null,
    findings: [],
    line_count: 3,
    part_count: 3,
    pending_count: 3,
    decided_count: 0,
    lines: [
      { ...govde, id: "e2e-quote-line-1", qty: 1, material: "S355", stock_item_id: null },
      { ...kol, id: "e2e-quote-line-2", qty: 2, material: "S355", stock_item_id: null, supplier_ask: null },
      third,
    ],
  };
}

/**
 * Make-or-buy desk (`/rfq/e2e-make-buy/supply?e2e=1`).
 * Supplier asks omit `supplier_email` and `sent_at`: engine main does not return them.
 * Two decided make lines match the route suggestion and stay hidden until shown.
 */
export const E2E_MAKE_BUY_ID = "e2e-make-buy";

export function buildE2eMakeBuyLoads(): MachineLoad[] {
  const machines: Machine[] = [
    { id: "m-lathe", code: "LATHE", name: "CNC lathe (Okuma LB3000)", hourly_rate_try: 0, capacity_hours_per_day: 8, status: "active" },
    { id: "m-vmc", code: "VMC", name: "CNC vertical machining", hourly_rate_try: 0, capacity_hours_per_day: 8, status: "active" },
    { id: "m-laser", code: "LASER", name: "Fiber laser 6 kW", hourly_rate_try: 0, capacity_hours_per_day: 8, status: "active" },
    { id: "m-saw", code: "SAW", name: "Saw", hourly_rate_try: 0, capacity_hours_per_day: 8, status: "active" },
    { id: "m-deburr", code: "DEBURR", name: "Manual deburring", hourly_rate_try: 0, capacity_hours_per_day: 8, status: "active" },
  ];
  const order: WorkOrder = { id: "e2e-wo-load", wo_no: "WO-E2E-LOAD", status: "released" };
  const op = (opNo: number, code: string, machineId: string, hours: number): WorkOrderOperation => ({
    op_no: opNo,
    op_code: code,
    machine_id: machineId,
    hourly_rate_try: 0,
    est_setup_hrs: 0,
    est_run_hrs: hours,
    actual_hours: 0,
    qty_good: 0,
    qty_scrap: 0,
    status: "pending",
  });
  const ops = [
    op(1, "TORNA", "m-lathe", 115.2),
    op(2, "FREZE", "m-vmc", 85.2),
    op(3, "KESIM", "m-laser", 48),
    op(4, "KESIM", "m-saw", 42),
    op(5, "CAPAK", "m-deburr", 36),
  ];
  return machineLoads(machines, [order], new Map([[order.id!, ops]]), { workingDays: 15 });
}

const E2E_WORK_ORDERS: WorkOrder[] = [
  {
    id: "wo-e2e-1048",
    wo_no: "WO-1048",
    status: "released",
    part_no: "HSG-001",
    qty: 4,
    est_hours: 8.2,
    notes: "Housing batch",
    source_job_id: "job-e2e-housing",
    created_at: "2026-10-30T11:20:00Z",
  },
  {
    id: "wo-e2e-1042",
    wo_no: "WO-1042",
    status: "draft",
    part_no: "BRACKET-60",
    qty: 12,
    est_hours: 6.5,
    notes: "First article",
    source_job_id: "job-e2e-bracket",
    created_at: "2026-10-28T09:00:00Z",
  },
  {
    id: "wo-e2e-1055",
    wo_no: "WO-1055",
    status: "released",
    part_no: "FLANGE-12",
    qty: 2,
    est_hours: 4,
    source_job_id: "job-e2e-flange",
    created_at: "2026-10-26T08:00:00Z",
  },
  {
    id: "wo-e2e-1031",
    wo_no: "WO-1031",
    status: "completed",
    part_no: "PIN-20",
    qty: 20,
    est_hours: 3.1,
    source_job_id: "job-e2e-pin",
    created_at: "2026-10-18T14:00:00Z",
  },
];

function e2eOp(
  partial: Pick<WorkOrderOperation, "id" | "op_no" | "op_code" | "status"> & Partial<WorkOrderOperation>,
): WorkOrderOperation {
  return {
    hourly_rate_try: 0,
    est_setup_hrs: 0,
    est_run_hrs: 0,
    actual_hours: 0,
    qty_good: 0,
    qty_scrap: 0,
    ...partial,
  };
}

const E2E_SHOPS: Record<
  string,
  { operations: WorkOrderOperation[]; materials: WorkOrderMaterial[]; cost: WorkOrderCost | null }
> = {
  "wo-e2e-1048": {
    operations: [
      e2eOp({
        id: "op-1048-10",
        op_no: 10,
        op_code: "KESIM",
        label_tr: "Kesim",
        machine_code: "SAW",
        machine_name: "Saw",
        hourly_rate_try: 450,
        est_setup_hrs: 0.4,
        est_run_hrs: 1.2,
        actual_hours: 0,
        qty_good: 0,
        qty_scrap: 0,
        status: "pending",
      }),
      e2eOp({
        id: "op-1048-20",
        op_no: 20,
        op_code: "TORNA",
        label_tr: "Torna",
        machine_code: "LATHE",
        machine_name: "CNC lathe",
        hourly_rate_try: 900,
        est_setup_hrs: 0.5,
        est_run_hrs: 3,
        actual_hours: 1.6,
        qty_good: 1,
        qty_scrap: 0,
        status: "running",
      }),
      e2eOp({
        id: "op-1048-30",
        op_no: 30,
        op_code: "FREZE",
        label_tr: "Freze",
        machine_code: "VMC",
        machine_name: "CNC vertical machining",
        hourly_rate_try: 850,
        est_setup_hrs: 0.3,
        est_run_hrs: 2.2,
        actual_hours: 2.4,
        qty_good: 4,
        qty_scrap: 1,
        status: "done",
      }),
    ],
    materials: [
      { id: 1, part_no: "S355-PLATE", qty_required: 4, qty_issued: 2, unit_cost_try: 180 },
      { id: 2, part_no: "BOLT-M8", qty_required: 16, qty_issued: 16, unit_cost_try: 4 },
    ],
    cost: {
      material_cost_try: 960,
      labor_cost_try: 1840,
      total_cost_try: 2800,
      est_hours: 7.6,
      actual_hours: 4,
      hours_variance: -3.6,
      qty_good: 4,
      qty_scrap: 1,
      scrap_rate: 0.2,
      cost_per_good_unit_try: 700,
      est_cost_try: 2600,
      cost_variance_try: 200,
    },
  },
  "wo-e2e-1042": {
    operations: [
      e2eOp({
        id: "op-1042-10",
        op_no: 10,
        op_code: "KESIM",
        label_tr: "Kesim",
        machine_name: "Saw",
        est_setup_hrs: 0.3,
        est_run_hrs: 1.1,
        status: "pending",
      }),
    ],
    materials: [],
    cost: null,
  },
};

/** Demo work orders for `?e2e=1`. Fresh copies so a click cannot rewrite the fixture. */
export function buildE2eWorkOrders(): WorkOrder[] {
  return E2E_WORK_ORDERS.map((order) => ({ ...order }));
}

/** Demo routing, material and cost for one work order. Unknown ids stay empty. */
export function buildE2eWorkOrderShop(workOrderId: string): {
  operations: WorkOrderOperation[];
  materials: WorkOrderMaterial[];
  cost: WorkOrderCost | null;
} {
  const shop = E2E_SHOPS[workOrderId];
  if (!shop) return { operations: [], materials: [], cost: null };
  return {
    operations: shop.operations.map((op) => ({ ...op })),
    materials: shop.materials.map((row) => ({ ...row })),
    cost: shop.cost ? { ...shop.cost } : null,
  };
}

export function buildE2eMakeBuyRfq(now = Date.now()): RfqRecord {
  const due = new Date(now + 8 * 86_400_000 + 3_600_000).toISOString();
  const line = (partial: Pick<RfqLine, "id" | "line_no" | "qty" | "make_buy"> & Partial<RfqLine>): RfqLine => ({
    kind: "part",
    source_kind: "manual",
    unit: "adet",
    material: "S355",
    stock_item_id: 100 + partial.line_no,
    route_json: [],
    decision_status: "pending",
    ...partial,
  });
  const lines: RfqLine[] = [
    line({
      id: "e2e-mb-1",
      line_no: 1,
      part_no: "HSG-001",
      description: "Body tube",
      qty: 1,
      make_buy: "make",
      route_json: [
        { op: "KESIM", machine: "Saw", label_tr: "Testere", est_hours: 0.4 },
        { op: "TORNA", machine: "CNC lathe", label_tr: "CNC torna", est_hours: 2.1 },
      ],
      est_cost_try: 1846.4,
      est_lead_days: 5,
      decision_status: "pending",
    }),
    line({
      id: "e2e-mb-2",
      line_no: 2,
      part_no: "HSG-009",
      description: "Rod clevis pin",
      qty: 4,
      make_buy: "buy",
      route_json: [{ op: "TORNA", machine: "CNC lathe", label_tr: "CNC torna", est_hours: 0.6 }],
      est_cost_try: 473.2,
      decision_status: "decided",
      supplier_ask: { status: "waiting", supplier: "Yıldız Torna" },
    }),
    line({
      id: "e2e-mb-3",
      line_no: 3,
      part_no: "HSG-005",
      description: "Piston",
      qty: 1,
      make_buy: "undecided",
      route_json: [{ op: "TORNA", machine: "CNC lathe", label_tr: "CNC torna", est_hours: 1.5 }],
      est_cost_try: 942,
      est_lead_days: 14,
      decision_status: "pending",
      supplier_ask: { status: "waiting", supplier: "Yıldız Torna" },
    }),
    line({
      id: "e2e-mb-4",
      line_no: 4,
      part_no: "HSG-003",
      description: "Mounting flange",
      qty: 2,
      make_buy: "make",
      route_json: [
        { op: "KESIM", machine: "Fiber laser 6 kW", label_tr: "Fiber lazer", est_hours: 0.3 },
        { op: "FREZE", machine: "CNC vertical machining", label_tr: "CNC dik işleme", est_hours: 1.2 },
      ],
      est_cost_try: 1336.4,
      decision_status: "pending",
    }),
    line({
      id: "e2e-mb-5",
      line_no: 5,
      part_no: "HSG-010",
      description: "Nut M20",
      qty: 8,
      make_buy: "buy",
      route_json: [{ op: "BUY", label_tr: "Satın alma" }],
      decision_status: "decided",
      supplier_ask: {
        status: "answered",
        supplier: "Bossard",
        reply_unit_price_try: 7.04,
        reply_lead_days: 3,
        answered_at: "2026-10-01T08:20:00Z",
      },
    }),
    line({
      id: "e2e-mb-6",
      line_no: 6,
      part_no: "HSG-042",
      description: "Galvanizing",
      qty: 1,
      unit: "kg",
      make_buy: "outsource",
      route_json: [],
      decision_status: "decided",
      supplier_ask: {
        status: "answered",
        supplier: "Ege Galvaniz",
        reply_unit_price_try: 18,
        reply_lead_days: 2,
        answered_at: "2026-10-01T08:20:00Z",
      },
    }),
    line({
      id: "e2e-mb-7",
      line_no: 7,
      part_no: "HSG-020",
      description: "Rod seal",
      qty: 2,
      make_buy: "buy",
      route_json: [],
      decision_status: "pending",
      supplier_ask: { status: "draft", supplier: "Kaya CNC" },
    }),
    line({
      id: "e2e-mb-8",
      line_no: 8,
      part_no: "HSG-011",
      description: "End cap",
      qty: 2,
      make_buy: "make",
      route_json: [{ op: "FREZE", machine: "CNC vertical machining", label_tr: "CNC dik işleme", est_hours: 0.8 }],
      est_cost_try: 640,
      decision_status: "decided",
    }),
    line({
      id: "e2e-mb-9",
      line_no: 9,
      part_no: "HSG-012",
      description: "Bushing",
      qty: 2,
      make_buy: "make",
      route_json: [{ op: "TORNA", machine: "CNC lathe", label_tr: "CNC torna", est_hours: 0.5 }],
      est_cost_try: 410,
      decision_status: "decided",
    }),
  ];
  return {
    id: E2E_MAKE_BUY_ID,
    rfq_no: "RFQ-1194",
    title: "Hydraulic cylinder body",
    status: "estimating",
    customer_name: "Demir Hidrolik",
    customer_email: "demir@hidrolik.test",
    due_at: due,
    line_count: lines.length,
    part_count: lines.length,
    pending_count: lines.filter((row) => row.decision_status !== "decided").length,
    decided_count: lines.filter((row) => row.decision_status === "decided").length,
    findings: [],
    lines,
    created_at: "2026-09-28T10:00:00Z",
  };
}

/**
 * Demo stand-in for the send desk (`?e2e=1` only). Every value is a field
 * `GET /v1/rfqs/{id}` already returns. Send on this record stays in the
 * browser: it must not call `POST /v1/rfqs/{id}/send`.
 */
export const E2E_SEND_RFQ_ID = "e2e-send-1";

export function buildE2eSendRfq(): RfqRecord {
  const lines: RfqLine[] = Array.from({ length: 12 }, (_, index) => ({
    id: `e2e-send-line-${index + 1}`,
    line_no: index + 1,
    kind: "part" as const,
    source_kind: "upload_job" as const,
    source_job_id: "e2e-job-1",
    part_no: `HSG-${String(index + 1).padStart(3, "0")}`,
    description: index === 0 ? "Hydraulic cylinder body" : `Cylinder part ${index + 1}`,
    material: "S355",
    qty: 1,
    unit: "adet",
    make_buy: "make" as const,
    decision_status: "decided" as const,
    route_json: [{ op: "FREZE", setup_hrs: 0.5, run_hrs_per_unit: 0.2, est_hours: 0.7 }],
    est_cost_try: 900,
    est_lead_days: 16,
    est_hours: 0.7,
  }));
  return {
    id: E2E_SEND_RFQ_ID,
    rfq_no: "RFQ-1194",
    title: "Hydraulic cylinder body",
    status: "ready",
    customer_name: "Demir Hidrolik",
    customer_email: "kemal.arslan@demirhidrolik.com",
    notes: "HSG-009 material confirmation",
    due_at: "2026-11-12T00:00:00Z",
    approved_margin_pct: 22,
    suggested_margin_pct: 22,
    est_cost_try: 10800,
    est_lead_days: 16,
    quote_total_try: 13176,
    line_count: 12,
    part_count: 12,
    pending_count: 0,
    decided_count: 12,
    lines,
    sources: [
      { id: "e2e-send-src", kind: "cad", read_state: "read", original_name: "cylinder.step" },
    ],
    customer_replies: [],
    created_at: "2026-09-28T08:00:00Z",
    updated_at: "2026-10-01T07:42:00Z",
  };
}

export const E2E_OUTCOME_ID = "e2e-outcome-1";

const E2E_OUTCOME_EMAIL = "kemal@demirhidrolik.test";

export function buildE2eOutcomeRfq(): RfqRecord {
  const base = buildE2eRfq();
  return {
    ...base,
    id: E2E_OUTCOME_ID,
    rfq_no: "RFQ-1194",
    title: "Hydraulic cylinder body",
    status: "sent",
    customer_name: "Demir Hidrolik",
    customer_email: E2E_OUTCOME_EMAIL,
    sent_at: "2026-10-01T12:00:00Z",
    quote_total_try: 13237.08,
    quote_currency: "TRY",
    suggested_margin_pct: 22,
    approved_margin_pct: 22,
    notes: "Kemal Bey: “fiyatınız %10 yüksek kaldı, termin uygundu.”",
    outcome_reason: null,
    outcome_at: null,
  };
}

/**
 * Other quotes for the demo customer, so the outcome card can count real fields
 * (status, outcome_reason, approved_margin_pct). Not part of the RFQ list.
 * Six won margins average 21.8. Eight losses, four of them on price.
 */
export function buildE2eOutcomePeers(): RfqRecord[] {
  const wonMargins = [22, 21, 20, 24, 22, 22];
  const lostReasons: OutcomeReason[] = ["price", "price", "price", "price", "lead", "lead", "capacity", "no_bid"];
  const shell = (id: string, status: "won" | "lost", extra: Partial<RfqRecord>): RfqRecord => ({
    id,
    rfq_no: id,
    title: "Hydraulic cylinder body",
    status,
    customer_name: "Demir Hidrolik",
    customer_email: E2E_OUTCOME_EMAIL,
    quote_currency: "TRY",
    line_count: 0,
    pending_count: 0,
    decided_count: 0,
    lines: [],
    ...extra,
  });
  return [
    ...wonMargins.map((margin, index) =>
      shell(`e2e-outcome-won-${index}`, "won", { approved_margin_pct: margin, quote_total_try: 10000 + index }),
    ),
    ...lostReasons.map((reason, index) =>
      shell(`e2e-outcome-lost-${index}`, "lost", { outcome_reason: reason, quote_total_try: 8000 + index }),
    ),
  ];
}

/** Org stats the outcome card can show offline. Same shape as GET /v1/rfqs/stats. */
export function buildE2eRfqStats(): RfqStats {
  return { won: 21, lost: 34, cancelled: 0, open: 9, win_rate: 21 / 55 };
}

/**
 * Offline rows for the RFQ list (`?e2e=1`).
 *
 * Dates are relative to `now` so "last activity" stays readable. Parts are the
 * SLICRANK fixture lines. Job ids are dropped: the demo has no baked thumbnails,
 * and the list must not ask the engine for an artifact that is not there.
 * One draft carries ten of those lines so the "+N" tail has something to show.
 */
export function buildE2eRfqList(now = new Date()): RfqRecord[] {
  const at = (msAgo: number) => new Date(now.getTime() - msAgo).toISOString();
  const hour = 3_600_000;
  const day = 24 * hour;
  const ahead = (days: number) => new Date(now.getTime() + days * day).toISOString();
  const base = buildE2eRfq();
  const two = (base.lines ?? []).map((line, index) => ({
    ...line,
    id: `e2e-list-part-${index}`,
    thumbnail_url: null,
    source_job_id: null,
    source_item_no: null,
  }));
  const uncosted = two.map((line) => ({ ...line, est_cost_try: null }));
  const many = Array.from({ length: 10 }, (_, index) => {
    const src = two[index % two.length];
    const base = {
      ...src,
      id: `e2e-list-many-${index}`,
      line_no: index + 1,
      decision_status: "pending" as const,
      supplier_ask: null,
      est_cost_try: null as number | null,
    };
    // First eight stay visible at 1440: amber, check, cross, check, none, check, costed, none.
    if (index === 0) return { ...base, supplier_ask: { status: "waiting" as const, supplier: "Öz Metal" } };
    if (index === 2) return { ...base, decision_status: "skipped" as const };
    if (index === 6) return { ...base, decision_status: "decided" as const, est_cost_try: 480 };
    if (index === 1 || index === 3 || index === 5) return { ...base, decision_status: "decided" as const };
    return base;
  });
  const marked = [
    {
      ...two[0],
      id: "e2e-list-1042-a",
      est_cost_try: 2063,
      decision_status: "decided" as const,
      supplier_ask: null,
    },
    {
      ...two[1],
      id: "e2e-list-1042-b",
      est_cost_try: null,
      decision_status: "pending" as const,
      supplier_ask: null,
    },
  ];
  const row = (
    partial: Partial<RfqRecord> & Pick<RfqRecord, "id" | "rfq_no" | "status">,
  ): RfqRecord => ({
    ...base,
    title: "SLICRANK MECHANISM",
    customer_name: "Acme",
    customer_email: "buyer@acme.test",
    quote_currency: "TRY",
    quote_total_try: null,
    part_thumbs: [],
    lines: two,
    line_count: two.length,
    part_count: two.length,
    pending_count: two.filter((line) => line.decision_status === "pending").length,
    decided_count: two.filter((line) => line.decision_status !== "pending").length,
    ...partial,
  });
  return [
    row({
      id: "e2e-list-1042",
      rfq_no: "RFQ-E2E-1042",
      status: "draft",
      lines: marked,
      line_count: marked.length,
      part_count: marked.length,
      pending_count: 1,
      decided_count: 1,
      sources: [
        {
          id: "e2e-src-1042-b",
          kind: "cad",
          read_state: "failed",
          original_name: "kol.step",
          line_ids: ["e2e-list-1042-b"],
        },
      ],
      due_at: ahead(12),
      created_at: at(2 * hour),
      updated_at: at(20 * 60_000),
    }),
    row({
      id: "e2e-list-1038",
      rfq_no: "RFQ-E2E-1038",
      status: "estimating",
      lines: uncosted,
      customer_name: null,
      due_at: at(4 * day),
      created_at: at(6 * day),
      updated_at: at(day),
    }),
    row({
      id: "e2e-list-1031",
      rfq_no: "RFQ-E2E-1031",
      status: "estimating",
      due_at: ahead(3),
      created_at: at(14 * day),
      updated_at: at(6 * day),
    }),
    row({
      id: "e2e-list-1024",
      rfq_no: "RFQ-E2E-1024",
      status: "ready",
      quote_total_try: 18420,
      quote_currency: "TRY",
      due_at: ahead(6),
      created_at: at(20 * day),
      updated_at: at(8 * day),
    }),
    // Stored status says ready, but nothing was uploaded or parsed. The list
    // must not call this Quoted (dev RFQ 0012).
    row({
      id: "e2e-list-1020",
      rfq_no: "RFQ-E2E-1020",
      status: "ready",
      title: "Empty request",
      lines: [],
      line_count: 0,
      part_count: 0,
      pending_count: 0,
      decided_count: 0,
      part_thumbs: [],
      sources: [],
      quote_total_try: null,
      due_at: ahead(9),
      created_at: at(40 * day),
      updated_at: at(40 * day),
    }),
    row({
      id: "e2e-list-1018",
      rfq_no: "RFQ-E2E-1018",
      status: "sent",
      quote_total_try: 22100,
      quote_currency: "EUR",
      due_at: at(2 * day),
      created_at: at(30 * day),
      updated_at: at(15 * day),
      sent_at: at(15 * day),
    }),
    row({
      id: "e2e-list-1012",
      rfq_no: "RFQ-E2E-1012",
      status: "draft",
      title: "Unassigned plate",
      customer_name: null,
      customer_email: null,
      lines: [],
      line_count: 0,
      part_count: 0,
      pending_count: 0,
      decided_count: 0,
      quote_total_try: null,
      due_at: ahead(9),
      created_at: at(day),
      updated_at: at(day),
    }),
    row({
      id: "e2e-list-1007",
      rfq_no: "RFQ-E2E-1007",
      status: "draft",
      due_at: ahead(21),
      lines: many,
      line_count: many.length,
      part_count: many.length,
      pending_count: many.filter((line) => line.decision_status === "pending").length,
      decided_count: many.filter((line) => line.decision_status !== "pending").length,
      created_at: at(3 * day),
      updated_at: at(3 * day),
    }),
  ];
}

/**
 * Offline board for the home dashboard (`?e2e=1`).
 *
 * Dates are relative to `now` so the sent/won chart and the follow-up age stay
 * populated. Counts are fields the engine already returns (`status`, `sent_at`,
 * `outcome_at`, `stock_missing_count`, `suggested_margin_pct`, `findings`).
 * Two won and three lost make the demo win rate 40% — derived, not typed in.
 */
export function buildE2eHomeRfqs(now = new Date()): RfqRecord[] {
  const at = (msAgo: number) => new Date(now.getTime() - msAgo).toISOString();
  const hour = 3_600_000;
  const day = 24 * hour;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(12, 0, 0, 0);
  const yesterdayIso = yesterday.toISOString();
  const base = buildE2eRfq();
  // source_kind stays upload_job so a won/ready status can display. The job
  // id is cleared so the thumb does not request a render that is not there.
  const drawn = (base.lines ?? []).map((line) => ({
    ...line,
    thumbnail_url: null,
    source_job_id: null,
    source_item_no: null,
  }));
  const uncosted = drawn.map((line) => ({ ...line, est_cost_try: null }));
  const row = (
    partial: Partial<RfqRecord> & Pick<RfqRecord, "id" | "rfq_no" | "title" | "status">,
  ): RfqRecord => ({
    ...base,
    quote_total_try: null,
    suggested_margin_pct: null,
    approved_margin_pct: null,
    est_cost_try: null,
    stock_missing_count: 0,
    findings: undefined,
    customer_replies: undefined,
    lines: partial.status === "estimating" ? uncosted : drawn,
    line_count: base.line_count,
    part_count: base.line_count,
    pending_count: base.pending_count,
    decided_count: base.decided_count,
    ...partial,
  });
  return [
    row({
      id: "e2e-home-15",
      rfq_no: "RFQ-E2E-15",
      title: "SLICRANK MECHANISM",
      status: "estimating",
      stock_missing_count: 3,
      suggested_margin_pct: 22,
      est_cost_try: 10850,
      created_at: at(2 * day),
      updated_at: at(14 * 60_000),
    }),
    row({
      id: "e2e-home-14",
      rfq_no: "RFQ-E2E-14",
      title: "5011T141_Worm-Drive Clamp for Firm Hose and Tube",
      status: "waiting_customer",
      customer_name: "Atlas Konveyör",
      waiting_at: at(2 * hour),
      updated_at: at(hour),
      created_at: at(6 * day),
      findings: [
        { code: "qty_missing", kind: "missing", field: "qty", message: "adet", line_id: "a" },
        { code: "qty_missing", kind: "missing", field: "qty", message: "adet", line_id: "b" },
        { code: "due_at_missing", kind: "missing", field: "due_at", message: "tarih" },
      ],
    }),
    row({
      id: "e2e-home-13",
      rfq_no: "RFQ-E2E-13",
      title: "Hydraulic tank lid",
      status: "sent",
      customer_name: "Demir Hidrolik",
      quote_total_try: 12905,
      approved_margin_pct: 18,
      sent_at: at(3 * day + hour),
      created_at: at(10 * day),
      updated_at: at(3 * day + hour),
      customer_replies: [],
    }),
    row({
      id: "e2e-home-12",
      rfq_no: "RFQ-E2E-12",
      title: "Vent plate",
      status: "ready",
      approved_margin_pct: 18,
      created_at: at(5 * day),
      updated_at: at(3 * hour),
    }),
    row({
      id: "e2e-home-11",
      rfq_no: "RFQ-E2E-11",
      title: "Motor bracket",
      status: "won",
      sent_at: at(10 * day),
      outcome_at: yesterdayIso,
      created_at: at(20 * day),
      updated_at: yesterdayIso,
    }),
    row({
      id: "e2e-home-lost-1",
      rfq_no: "RFQ-E2E-L1",
      title: "Lost cover",
      status: "lost",
      sent_at: at(12 * day),
      outcome_at: at(11 * day),
      created_at: at(18 * day),
      updated_at: at(11 * day),
    }),
    row({
      id: "e2e-home-won-2",
      rfq_no: "RFQ-E2E-W2",
      title: "Won flange",
      status: "won",
      sent_at: at(20 * day),
      outcome_at: at(18 * day),
      created_at: at(24 * day),
      updated_at: at(18 * day),
    }),
    row({
      id: "e2e-home-lost-2",
      rfq_no: "RFQ-E2E-L2",
      title: "Lost pin",
      status: "lost",
      sent_at: at(22 * day),
      outcome_at: at(21 * day),
      created_at: at(26 * day),
      updated_at: at(21 * day),
    }),
    row({
      id: "e2e-home-lost-3",
      rfq_no: "RFQ-E2E-L3",
      title: "Lost bush",
      status: "lost",
      sent_at: at(8 * day),
      outcome_at: at(7 * day),
      created_at: at(14 * day),
      updated_at: at(7 * day),
    }),
  ];
}

function demoLine(p: Partial<RfqLine> & Pick<RfqLine, "id">): RfqLine {
  return {
    line_no: 1,
    kind: "part",
    source_kind: "upload_job",
    qty: 1,
    make_buy: "make",
    route_json: [],
    decision_status: "pending",
    ...p,
  };
}

/**
 * Offline customers for `/?e2e=1`.
 *
 * Each row is an RFQ the demo already knows how to describe. Customers are
 * whatever `customersFromRfqs` groups out of these — there is no separate
 * customer record. The nameless RFQ is here so the list can prove it is not
 * turned into a company.
 */
export function buildE2eCustomerRfqs(): RfqRecord[] {
  const shell = (p: Partial<RfqRecord> & Pick<RfqRecord, "id" | "rfq_no" | "status">): RfqRecord => ({
    line_count: p.lines?.length ?? 0,
    pending_count: 0,
    decided_count: 0,
    lines: [],
    quote_total_try: null,
    ...p,
  });

  return [
    shell({
      id: "e2e-cust-2201",
      rfq_no: "RFQ-E2E-2201",
      title: "SLICRANK MECHANISM",
      status: "sent",
      customer_name: "Acme",
      customer_email: "buyer@acme.test",
      due_at: "2026-10-15T00:00:00Z",
      quote_total_try: 18400,
      quote_currency: "TRY",
      target_price: 17000,
      created_at: "2026-09-02T08:00:00Z",
      sent_at: "2026-09-04T08:00:00Z",
      updated_at: "2026-09-04T08:00:00Z",
      findings: [{ code: "cad_failed", kind: "failed", message: "Could not read KOL", line_id: "e2e-cust-2201-b" }],
      lines: [
        demoLine({
          id: "e2e-cust-2201-a",
          line_no: 1,
          part_no: "GOVDE-340X92X40",
          description: "Kızak Mekanizması Gövdesi",
          source_kind: "upload_job",
          source_job_id: "e2e-job-1",
          source_item_no: 1,
          est_cost_try: 2063,
          decision_status: "decided",
        }),
        demoLine({
          id: "e2e-cust-2201-b",
          line_no: 2,
          part_no: "KOL-325X80X50",
          description: "Ana Bağlantı Kolu",
          source_kind: "upload_job",
          decision_status: "pending",
        }),
        demoLine({
          id: "e2e-cust-2201-c",
          line_no: 3,
          part_no: "PIN-8",
          description: "Dowel pin",
          source_kind: "manual",
          make_buy: "buy",
          decision_status: "pending",
          supplier_ask: { status: "waiting", supplier: "Öz Metal" },
        }),
      ],
    }),
    shell({
      id: "e2e-cust-2188",
      rfq_no: "RFQ-E2E-2188",
      title: "Bracket redo",
      status: "estimating",
      customer_name: "Acme",
      customer_email: "buyer@acme.test",
      due_at: "2026-10-01T00:00:00Z",
      est_cost_try: 99999,
      created_at: "2026-08-20T08:00:00Z",
      updated_at: "2026-09-28T08:00:00Z",
      lines: [
        demoLine({
          id: "e2e-cust-2188-a",
          part_no: "BRK-40",
          description: "Bracket",
          est_cost_try: 860,
          decision_status: "decided",
          source_kind: "catalog_part",
        }),
      ],
    }),
    shell({
      id: "e2e-cust-2104",
      rfq_no: "RFQ-E2E-2104",
      title: "Housing batch",
      status: "won",
      customer_name: "Acme",
      customer_email: "buyer@acme.test",
      quote_total_try: 9200,
      quote_currency: "TRY",
      created_at: "2026-06-01T08:00:00Z",
      sent_at: "2026-06-03T08:00:00Z",
      outcome_at: "2026-06-10T08:00:00Z",
      lines: [
        demoLine({
          id: "e2e-cust-2104-a",
          part_no: "HSG-12",
          description: "Housing",
          est_cost_try: 4100,
          decision_status: "decided",
          source_kind: "intake",
        }),
      ],
    }),
    shell({
      id: "e2e-cust-2088",
      rfq_no: "RFQ-E2E-2088",
      title: "Flanş 120",
      status: "won",
      customer_name: "Ahmet Yılmaz",
      customer_email: "ahmet@musteri.com",
      quote_total_try: 4100,
      quote_currency: "TRY",
      due_at: "2026-08-01T00:00:00Z",
      created_at: "2026-07-01T08:00:00Z",
      sent_at: "2026-07-01T14:00:00Z",
      outcome_at: "2026-07-08T08:00:00Z",
      customer_replies: [{ id: "e2e-reply-1", body: "Ölçü 120 mm uygun.", created_at: "2026-07-02T11:00:00Z" }],
      lines: [
        demoLine({
          id: "e2e-cust-2088-a",
          part_no: "FLANS-120",
          description: "Flanş",
          qty: 12,
          est_cost_try: 1800,
          decision_status: "decided",
          source_kind: "intake",
        }),
      ],
    }),
    shell({
      id: "e2e-cust-2071",
      rfq_no: "RFQ-E2E-2071",
      title: "Mil 30",
      status: "lost",
      customer_name: "Ahmet Yılmaz",
      customer_email: "ahmet@musteri.com",
      quote_total_try: 1500,
      quote_currency: "TRY",
      outcome_reason: "price",
      created_at: "2026-05-10T08:00:00Z",
      sent_at: "2026-05-12T08:00:00Z",
      outcome_at: "2026-05-20T08:00:00Z",
      lines: [
        demoLine({
          id: "e2e-cust-2071-a",
          part_no: "MIL-30",
          description: "Mil",
          est_cost_try: 640,
          decision_status: "decided",
          source_kind: "intake",
        }),
      ],
    }),
    shell({
      id: "e2e-cust-2190",
      rfq_no: "RFQ-E2E-2190",
      title: "Cover plate",
      status: "ready",
      customer_name: "Borusan",
      customer_email: "satinalma@borusan.com.tr",
      quote_total_try: 6400,
      quote_currency: "EUR",
      target_price: 6000,
      due_at: "2026-10-20T00:00:00Z",
      created_at: "2026-09-12T08:00:00Z",
      updated_at: "2026-09-18T08:00:00Z",
      lines: [
        demoLine({
          id: "e2e-cust-2190-a",
          part_no: "CVR-2",
          description: "Cover plate",
          est_cost_try: 2200,
          decision_status: "decided",
          source_kind: "catalog_part",
        }),
      ],
    }),
    shell({
      id: "e2e-cust-2160",
      rfq_no: "RFQ-E2E-2160",
      title: "Kapak",
      status: "draft",
      customer_name: "Borusan",
      customer_email: "satinalma@borusan.com.tr",
      created_at: "2026-09-01T08:00:00Z",
      lines: [
        demoLine({
          id: "e2e-cust-2160-a",
          part_no: "KPK-1",
          description: "Kapak",
          source_kind: "manual",
        }),
      ],
    }),
    shell({
      id: "e2e-cust-2210",
      rfq_no: "RFQ-E2E-2210",
      title: "Pin set",
      status: "draft",
      customer_email: "ops@kuzey.example",
      created_at: "2026-08-01T08:00:00Z",
      updated_at: "2026-08-02T08:00:00Z",
      lines: [
        demoLine({
          id: "e2e-cust-2210-a",
          part_no: "PIN-SET",
          description: "Pin set",
          source_kind: "manual",
        }),
      ],
    }),
    shell({
      id: "e2e-cust-2001",
      rfq_no: "RFQ-E2E-2001",
      title: "Should not appear",
      status: "won",
      customer_name: "Ghost Shop",
      customer_email: null,
      quote_total_try: 1,
      created_at: "2026-09-30T08:00:00Z",
    }),
  ];
}

/** Inbox rows the demo can link to those RFQs. Unlinked mail stays off the timeline. */
export function buildE2eCustomerMail(): MailMessage[] {
  return [
    {
      id: "e2e-mail-acme",
      direction: "outbound",
      purpose: "quote",
      rfq_id: "e2e-cust-2201",
      subject: "Quote RFQ-E2E-2201",
      to: "buyer@acme.test",
      sent_at: "2026-09-04T08:00:00Z",
    },
    {
      id: "e2e-mail-ahmet",
      direction: "inbound",
      purpose: "customer_question",
      rfq_id: "e2e-cust-2088",
      from_address: "ahmet@musteri.com",
      subject: "Re: RFQ-E2E-2088",
      received_at: "2026-09-20T09:00:00Z",
    },
    {
      id: "e2e-mail-stray",
      direction: "inbound",
      rfq_id: null,
      from_address: "news@example.com",
      subject: "Newsletter",
      received_at: "2026-09-29T00:00:00Z",
    },
  ];
}

/**
 * One machine so the default `?e2e=1` board counts as a finished workshop.
 * Fresh and partial onboarding fixtures omit or include this on purpose.
 */
export function buildE2eHomeMachines(): Machine[] {
  return [
    {
      id: "e2e-machine-mill",
      code: "VMC-850",
      name: "Vertical machining centre",
      kind: "milling",
      hourly_rate_try: 950,
      capacity_hours_per_day: 8,
      status: "active",
    },
  ];
}

/** `?e2e=1` is the returning workshop. `onboarding=fresh|partial` is a new account. */
export type E2eOnboardingFixture = "ready" | "fresh" | "partial";

export function e2eOnboardingFixture(): E2eOnboardingFixture {
  if (typeof window === "undefined") return "ready";
  try {
    const mode = new URLSearchParams(window.location.search).get("onboarding");
    if (mode === "fresh" || mode === "partial") return mode;
  } catch {
    /* ignore */
  }
  return "ready";
}

/** Shop rows behind the offline stock-match sheet. Part numbers and quantities come from the fixture's matched_stock candidates. */
export function buildE2eStock(): ErpStockItem[] {
  const preferred = items[0].matched_stock;
  const candidates = preferred?.candidates ?? [];
  return candidates
    .filter((candidate) => candidate.part_no)
    .map((candidate, index) => ({
      id: index + 1,
      part_no: candidate.part_no,
      description: index === 0 ? preferred?.description ?? null : null,
      material: items[0].material,
      unit: items[0].unit,
      qty_on_hand: candidate.stock_qty ?? 0,
      qty_reserved: 0,
      qty_available: candidate.stock_qty ?? 0,
    }));
}

/** Deterministic assembly forecast for Playwright + Bug Pack demos. */
export function buildE2eMockForecast(): ForecastState {
  return {
    drawing_id: "e2e-slicrank",
    document_name: "SLICRANK MECHANISM.STEP",
    status: "READY",
    items,
    confidence_overall: 0.85,
    parse_duration_ms: 120,
    job_id: "e2e-job-1",
    detection_method: "neural_step_bridge:assembly",
    risk_summary: { green: 0, yellow: 2, red: 0 },
    total_est_material_cost_try: 1200,
    total_production_cost_try: 1978,
    est_lead_time_days: 3,
    production_summary_tr:
      "2 parça montaj: gövde kritik yolda (~2 gün). Malzeme S355; stoktan ERP-GOVDE-01 tercih.",
    production_intel: {
      provider: "heuristic",
      confidence: 0.84,
      drawing_total_production_cost_try: 1978,
      material_cost_try: 480,
      machining_cost_try: 520,
      labor_cost_try: 375,
      est_lead_time_days: 3,
      // Direct costs 480+520+375 = 1,375. Per-item overhead sums to 343.75.
      // The total is 1,978: the estimator folds a margin into
      // total_production_cost_try and does not return that margin as a field.
      machining_breakdown: {
        cutting: 120,
        milling: 250,
        drilling: 150,
      },
      material_pricing: {
        s355: {
          material_class: "S355",
          label_tr: "S355 yapı çeliği",
          usd_per_kg: 0.92,
          try_per_kg: 32.4,
          density_kg_m3: 7850,
          fx_rate: 35.2,
          fx_currency: "USD",
          rate_date: "2026-07-20",
          rate_source: "tcmb",
          stale: false,
        },
      },
      per_item_costs: [
        {
          price_source: "erp_stock",
          material_class: "S355",
          material_cost_try: 280,
          machining_cost_try: 320,
          labor_cost_try: 220,
          overhead_cost_try: 205,
          total_production_cost_try: 1179,
        },
        {
          price_source: "market_rate",
          material_class: "S355",
          material_cost_try: 200,
          machining_cost_try: 200,
          labor_cost_try: 155,
          overhead_cost_try: 138.75,
          total_production_cost_try: 799,
        },
      ],
      actions: [
        {
          action_type: "MAKE_INTERNAL",
          description_tr: "Gövde freze + delik operasyonu",
          est_cost_try: 3200,
          est_hours: 2.1,
          est_lead_days: 2,
          target_part_no: "GOVDE-340X92X40",
        },
        {
          action_type: "MAKE_INTERNAL",
          description_tr: "Kol kesim + tornalama",
          est_cost_try: 1900,
          est_hours: 1.1,
          est_lead_days: 2,
          target_part_no: "KOL-325X80X50",
        },
      ],
      per_part_plans: [
        {
          item_no: 1,
          part_no: "GOVDE-340X92X40",
          name: "Solid_1",
          part_type: "govde",
          position_mm: [0, 0, 0],
          operations: [
            { op: "KESIM", label_tr: "Kesim", est_hours: 0.35 },
            { op: "FREZE", label_tr: "Frezeleme", est_hours: 1.2 },
          ],
          est_total_hours: 1.55,
          est_cost_try: 1179,
          est_lead_days: 2,
          llm_refined: false,
        },
        {
          item_no: 2,
          part_no: "KOL-325X80X50",
          name: "Solid_2",
          part_type: "buyuk bilesen",
          position_mm: [-55, 5, 30],
          operations: [
            { op: "KESIM", label_tr: "Kesim", est_hours: 0.3 },
            { op: "TORNA", label_tr: "Tornalama", est_hours: 0.8 },
          ],
          est_total_hours: 1.1,
          est_cost_try: 799,
          est_lead_days: 2,
          llm_refined: false,
        },
      ],
      assembly_plan: {
        parallel_parts: 2,
        critical_part_no: "GOVDE-340X92X40",
        assembly_hours: 0.9,
        est_total_days: 3,
        sequence_tr:
          "2 parça paralel işlenir (ERP-GOVDE-01, KOL-325X80X50); kritik parça ERP-GOVDE-01 (~2 gün), ardından montaj.",
      },
      scheduling_stub: {
        est_total_days: 3,
        critical_path: ["Gövde", "Montaj"],
        steps: [
          { name: "Kesim", hours: 0.5, type: "MACHINE", start_day: 0, end_day: 0.5 },
          { name: "Freze", hours: 1.2, type: "MACHINE", start_day: 0.5, end_day: 1.5 },
          { name: "Montaj", hours: 0.9, type: "MACHINE", start_day: 2, end_day: 3 },
        ],
      },
      dfm: {
        summary: {
          score: 78,
          critical_count: 0,
          warning_count: 2,
          info_count: 1,
        },
        issues: [
          {
            id: "dfm-hole-depth",
            severity: "warning",
            category: "drill",
            title_tr: "Derin delik oranı yüksek",
            detail_tr: "Gövde üzerinde L/D > 5 delikler freze yerine matkap gerektirebilir.",
            suggested_fix_tr: "Delik sırasını 2 kademeli delme olarak planlayın.",
            feature_ref: { item_no: 1, solid_index: 0, part_no: "GOVDE-340X92X40" },
            estimated_cost_impact_try: 180,
          },
          {
            id: "dfm-stock",
            severity: "warning",
            category: "stock",
            title_tr: "Kol için stok eşleşmesi zayıf",
            detail_tr: "KOL-325X80X50 için tercih kaydı yok; boyut eşleşmesi %78.",
            suggested_fix_tr: "ERP stok tercihini kaydedin veya ham kesit stokunu doğrulayın.",
            feature_ref: { item_no: 2, solid_index: 1, part_no: "KOL-325X80X50" },
            estimated_cost_impact_try: 0,
          },
          {
            id: "dfm-parallel",
            severity: "info",
            category: "schedule",
            title_tr: "Paralel işlenebilir",
            detail_tr: "İki solid aynı anda freze hücrelerinde koşturulabilir.",
            feature_ref: {},
          },
        ],
        cost_drivers: [
          {
            id: "mill",
            title_tr: "Freze süresi",
            amount_try: 250,
            share_pct: 13,
            note_tr: "Gövde yüzey frezeleme baskın.",
          },
          {
            id: "material",
            title_tr: "Hammadde",
            amount_try: 480,
            share_pct: 24,
            note_tr: "S355 stok fiyatı.",
          },
        ],
      },
    },
    dxf_features: {
      classification: "assembly_or_multi_solid",
      derived: {
        is_real_3d: true,
        volume_m3: 0.00094,
        solid_count: 2,
        complexity_score: 22,
        machining_hints: { detected_holes: 12 },
      },
      geo: { hole_count: 12 },
    },
  };
}

export function slimForecastForBugPack(f: ForecastState | null | undefined): Record<string, unknown> {
  if (!f) return { empty: true };
  const items = (f.items || []).slice(0, 40).map((it) => ({
    item_no: it.item_no,
    part_no: it.part_no,
    description: it.description,
    risk_level: it.risk_level,
    suggested_action: it.suggested_action,
    matched_stock: it.matched_stock
      ? {
          part_no: it.matched_stock.part_no,
          match_score: it.matched_stock.match_score,
          match_type: it.matched_stock.match_type,
          stock_qty: it.matched_stock.stock_qty,
          candidates: (it.matched_stock.candidates || []).slice(0, 5).map((c) => ({
            part_no: c.part_no,
            match_score: c.match_score,
          })),
        }
      : null,
    raw_source: (it.raw_row_data as { source?: string } | null)?.source ?? null,
  }));
  const pi = (f.production_intel || {}) as Record<string, unknown>;
  const plans = Array.isArray(pi.per_part_plans)
    ? (pi.per_part_plans as Array<Record<string, unknown>>).slice(0, 40).map((p) => ({
        item_no: p.item_no,
        part_no: p.part_no,
        name: p.name,
        part_type: p.part_type,
        est_cost_try: p.est_cost_try,
        est_lead_days: p.est_lead_days,
      }))
    : [];
  return {
    drawing_id: f.drawing_id,
    document_name: f.document_name,
    status: f.status,
    job_id: f.job_id,
    detection_method: f.detection_method,
    cache_stale: f.cache_stale,
    cache_issues: f.cache_issues,
    risk_summary: f.risk_summary,
    items,
    production_intel: {
      drawing_total_production_cost_try: pi.drawing_total_production_cost_try,
      material_cost_try: pi.material_cost_try,
      est_lead_time_days: pi.est_lead_time_days,
      provider: pi.provider,
      assembly_plan: pi.assembly_plan,
      per_part_plans: plans,
    },
  };
}

function e2eRun(over: Partial<HighwayRunSummary> & Pick<HighwayRunSummary, "id" | "rfq_id">): HighwayRunSummary {
  return {
    rfq_no: null,
    rfq_title: null,
    customer_name: null,
    rfq_status: "estimating",
    status: "waiting",
    trigger: "manual",
    current_step: null,
    gate: null,
    gate_reason: null,
    error: null,
    waiting_for: "human",
    current_agent: null,
    started_at: "2026-11-04T08:10:00Z",
    updated_at: "2026-11-04T09:40:00Z",
    finished_at: null,
    ...over,
  };
}

/** GET /v1/highway/runs for the Agents page: two people gates, one job, one failure. */
export function buildE2eHighwayRuns(): HighwayRunsResponse {
  const items = [
    e2eRun({
      id: "e2e-run-margin",
      rfq_id: E2E_SEND_RFQ_ID,
      rfq_no: "RFQ-1201",
      rfq_title: "Flange set",
      customer_name: "Demir Hidrolik",
      current_step: "margin",
      gate: "margin_approval",
      gate_reason: "margin suggested; a person approves it",
      current_agent: "8 Ticari (öneri)",
    }),
    e2eRun({
      id: "e2e-run-triage",
      rfq_id: E2E_TRIAGE_ID,
      rfq_no: "RFQ-1198",
      rfq_title: "Pump housing",
      customer_name: "Akın Makina",
      current_step: "triage",
      gate: "customer_question",
      gate_reason: "quantity missing",
      current_agent: "3 Triyaj",
      updated_at: "2026-11-04T09:20:00Z",
    }),
    e2eRun({
      id: "e2e-run-parse",
      rfq_id: E2E_MAKE_BUY_ID,
      rfq_no: "RFQ-1203",
      rfq_title: "Shaft batch",
      customer_name: "Yıldız Otomotiv",
      current_step: "geometry",
      gate: "parse_pending",
      waiting_for: "job",
      current_agent: "2 Geometri",
      updated_at: "2026-11-04T09:10:00Z",
    }),
    e2eRun({
      id: "e2e-run-failed",
      rfq_id: E2E_QUOTE_BLOCKED_ID,
      rfq_no: "RFQ-1190",
      rfq_title: null,
      customer_name: "Kaya Döküm",
      status: "failed",
      current_step: "cost",
      error: "persist_failed: rate book missing",
      waiting_for: null,
      current_agent: "gri: maliyet kodu",
      updated_at: "2026-11-04T08:55:00Z",
    }),
  ];
  return { items, count: items.length, autorun_enabled: false };
}

export const E2E_INBOUND_ADDRESS = "org_a.k3x9q2@rfq.semanticad.ai";
